import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, Calendar, Vote, 
  FileText, Bell, RefreshCw, Download, Send, Clock
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${API_URL}/api`;

// Color palette for charts
const COLORS = ['#D4AF37', '#3B82F6', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444'];

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [summary, setSummary] = useState(null);
  const [registrations, setRegistrations] = useState(null);
  const [voting, setVoting] = useState(null);
  const [events, setEvents] = useState(null);
  const [engagement, setEngagement] = useState(null);

  useEffect(() => {
    loadAllData();
  }, [dateRange]);

  const loadAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('session_token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [summaryRes, regRes, voteRes, eventRes, engageRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { headers }),
        axios.get(`${API}/analytics/registrations?days=${dateRange}`, { headers }),
        axios.get(`${API}/analytics/voting?days=${dateRange}`, { headers }),
        axios.get(`${API}/analytics/events`, { headers }),
        axios.get(`${API}/analytics/engagement?days=${dateRange}`, { headers })
      ]);

      setSummary(summaryRes.data);
      setRegistrations(regRes.data);
      setVoting(voteRes.data);
      setEvents(eventRes.data);
      setEngagement(engageRes.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const MetricCard = ({ title, value, subtitle, change, icon: Icon, color }) => (
    <Card className="hover-lift" style={{ borderTop: `3px solid ${color}` }}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className="h-4 w-4" style={{ color }} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold" style={{ color: '#0F172A' }}>{value?.toLocaleString() || 0}</div>
        <div className="flex items-center gap-2 mt-1">
          {change !== undefined && (
            <span className={`flex items-center text-xs font-medium ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {Math.abs(change)}%
            </span>
          )}
          {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: '#0F172A' }}>Analytics Dashboard</h2>
          <p className="text-muted-foreground">Performance insights and trends</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px]" data-testid="date-range-select">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={loadAllData} data-testid="refresh-analytics">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Registrations"
          value={summary?.registrations?.total}
          subtitle={`${summary?.registrations?.this_week || 0} this week`}
          change={summary?.registrations?.change_percent}
          icon={FileText}
          color="#D4AF37"
        />
        <MetricCard
          title="Total Votes"
          value={summary?.votes?.total}
          subtitle={`${summary?.votes?.this_week || 0} this week`}
          change={summary?.votes?.change_percent}
          icon={Vote}
          color="#3B82F6"
        />
        <MetricCard
          title="Active Events"
          value={summary?.active_events}
          icon={Calendar}
          color="#10B981"
        />
        <MetricCard
          title="Total Members"
          value={summary?.total_members}
          icon={Users}
          color="#8B5CF6"
        />
      </div>

      {/* Tabs for detailed analytics */}
      <Tabs defaultValue="registrations" className="space-y-4">
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="registrations" data-testid="tab-registrations">Registrations</TabsTrigger>
          <TabsTrigger value="voting" data-testid="tab-voting">Voting</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
          <TabsTrigger value="engagement" data-testid="tab-engagement">Engagement</TabsTrigger>
        </TabsList>

        {/* Registrations Tab */}
        <TabsContent value="registrations" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Trend Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Registration Trend</CardTitle>
                <CardDescription>Daily registrations over the selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={registrations?.trend || []}>
                    <defs>
                      <linearGradient id="colorReg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(val) => val?.slice(5) || ''}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #eee', borderRadius: '8px' }}
                      labelFormatter={(val) => `Date: ${val}`}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#D4AF37" 
                      fillOpacity={1} 
                      fill="url(#colorReg)"
                      name="Registrations"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Top Events */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Events</CardTitle>
                <CardDescription>By registration count</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(registrations?.top_events || []).slice(0, 5).map((event, idx) => (
                    <div key={event.event_id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" 
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}>
                          {idx + 1}
                        </span>
                        <span className="text-sm truncate max-w-[150px]" title={event.title}>
                          {event.title}
                        </span>
                      </div>
                      <span className="font-semibold text-sm">{event.count}</span>
                    </div>
                  ))}
                  {(!registrations?.top_events || registrations.top_events.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">No data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Voting Tab */}
        <TabsContent value="voting" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Voting Trend */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Voting Trend</CardTitle>
                <CardDescription>Daily votes over the selected period</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={voting?.trend || []}>
                    <defs>
                      <linearGradient id="colorVote" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(val) => val?.slice(5) || ''}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #eee', borderRadius: '8px' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#3B82F6" 
                      fillOpacity={1} 
                      fill="url(#colorVote)"
                      name="Votes"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Top Nominees */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Nominees</CardTitle>
                <CardDescription>By vote count</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {(voting?.top_nominees || []).slice(0, 5).map((nominee, idx) => (
                    <div key={nominee.nominee_id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" 
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}>
                          {idx + 1}
                        </span>
                        <div className="flex flex-col">
                          <span className="text-sm font-medium truncate max-w-[120px]" title={nominee.name}>
                            {nominee.name}
                          </span>
                          <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                            {nominee.event}
                          </span>
                        </div>
                      </div>
                      <span className="font-semibold text-sm">{nominee.votes}</span>
                    </div>
                  ))}
                  {(!voting?.top_nominees || voting.top_nominees.length === 0) && (
                    <p className="text-sm text-muted-foreground text-center py-4">No data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Votes by Event */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Votes by Event</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={voting?.by_event || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis 
                    type="category" 
                    dataKey="title" 
                    tick={{ fontSize: 11 }} 
                    width={150}
                    tickFormatter={(val) => val?.length > 20 ? val.slice(0, 20) + '...' : val}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#fff', border: '1px solid #eee', borderRadius: '8px' }}
                  />
                  <Bar dataKey="votes" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Events Tab */}
        <TabsContent value="events" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Event Status</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={events?.status_distribution || []}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="status"
                    >
                      {(events?.status_distribution || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Category Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Event Categories</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={events?.category_distribution || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis 
                      dataKey="category" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(val) => val?.length > 12 ? val.slice(0, 12) + '...' : val}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Events Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Event Performance</CardTitle>
              <CardDescription>Top events by registrations and votes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Event</th>
                      <th className="text-left py-3 px-2">Status</th>
                      <th className="text-right py-3 px-2">Registrations</th>
                      <th className="text-right py-3 px-2">Votes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(events?.events || []).slice(0, 10).map((event) => (
                      <tr key={event.event_id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-2">
                          <div className="font-medium truncate max-w-[200px]" title={event.title}>
                            {event.title}
                          </div>
                          {event.date && (
                            <div className="text-xs text-muted-foreground">{event.date}</div>
                          )}
                        </td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            event.status === 'upcoming' ? 'bg-green-100 text-green-700' :
                            event.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {event.status}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-right font-medium">{event.registrations}</td>
                        <td className="py-3 px-2 text-right font-medium">{event.votes}</td>
                      </tr>
                    ))}
                    {(!events?.events || events.events.length === 0) && (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-muted-foreground">
                          No events data available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Engagement Tab */}
        <TabsContent value="engagement" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Hourly Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Activity by Hour
                </CardTitle>
                <CardDescription>When users are most active</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={engagement?.hourly_distribution || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis 
                      dataKey="hour" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(val) => `${val}:00`}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      labelFormatter={(val) => `${val}:00 - ${val}:59`}
                    />
                    <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Day of Week Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Activity by Day
                </CardTitle>
                <CardDescription>Most active days of the week</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={engagement?.day_of_week_distribution || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#EC4899" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Push Notifications Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Push Notification Subscribers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-3xl font-bold" style={{ color: '#D4AF37' }}>
                    {engagement?.push_notifications?.total_subscriptions || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Subscribers</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-3xl font-bold" style={{ color: '#3B82F6' }}>
                    {engagement?.push_notifications?.registered_users || 0}
                  </div>
                  <div className="text-sm text-muted-foreground">Registered Users</div>
                </div>
                <div className="text-center p-4 bg-muted/50 rounded-lg">
                  <div className="text-3xl font-bold" style={{ color: '#10B981' }}>
                    {(engagement?.push_notifications?.total_subscriptions || 0) - (engagement?.push_notifications?.registered_users || 0)}
                  </div>
                  <div className="text-sm text-muted-foreground">Anonymous</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsPage;
